// 당일 클리닉 #/today — 그날 일정이 시간순 목록. 시험지 통과 체크 → 전부 통과면 자동 완료(서버 최종 판정) · 지각·노쇼 → 경고카드(서버 자동)
// compact 카드 1단 · medium 2열 · expanded 3열 · large 표(카드 ↔ 표 전환, 기본 표). 인쇄는 어느 폭이든 표(당일 관리표)
import { useState, type CSSProperties } from "react";
import { closeSession, setLate, toggleItem } from "../data/actions";
import { itemsFor, stats, statusVar, studentMap, upcoming } from "../data/select";
import { useView, type View } from "../data/store";
import { St, type Item, type Session, type Student } from "../data/types";
import { addDays, fmtDate, fmtDateLong, nowHM, todayStr } from "../lib/date";
import { go, usePref, useWidthClass, type Route } from "../ui/hooks";
import { Icon } from "../ui/icons";
import { Empty, Pill, StatTile, StatusPill, StudentName, toast } from "../ui/parts";
import { Loading } from "./Dashboard";

const FILTERS = ["전체", "대기", St.DONE, St.PARTIAL, St.NOSHOW] as const;
const match = (f: string, s: Session) => f === "전체" || s.status === (f === "대기" ? St.PLAN : f);

export function Today({ route }: { route: Route }) {
  const v = useView();
  const wc = useWidthClass();
  const td = todayStr();
  const date = route.query.get("date") && /^\d{4}-\d{2}-\d{2}$/.test(route.query.get("date")!) ? route.query.get("date")! : td;
  const setDate = (d: string) => go(d === td ? "today" : `today?date=${d}`);
  const [filter, setFilter] = useState<string>("전체");
  const [mode, setMode] = usePref<"table" | "cards">("centium.todayView", "table");
  if (!v.status.loaded) return <Loading />;

  const sm = studentMap(v);
  const all = v.sessions
    .filter((s) => s.date === date && sm.has(s.studentId))
    .sort((a, b) => a.time.localeCompare(b.time) || sm.get(a.studentId)!.name.localeCompare(sm.get(b.studentId)!.name, "ko"));
  const list = all.filter((s) => match(filter, s));
  const stt = stats(v, date);
  const table = wc === "large" && mode === "table";

  return (
    <div className="page today">
      <div className="datebar">
        <button type="button" className="btn btn-icon" aria-label="전날" onClick={() => setDate(addDays(date, -1))}><Icon name="left" /></button>
        <h2 className="datebar-t">{fmtDateLong(date)}</h2>
        <button type="button" className="btn btn-icon" aria-label="다음날" onClick={() => setDate(addDays(date, 1))}><Icon name="right" /></button>
        {date !== td ? <button type="button" className="btn btn-ghost" onClick={() => setDate(td)}>오늘</button> : null}
        <span className="grow" />
        {wc === "large" ? (
          <div className="seg" role="group" aria-label="보기 방식">
            <button type="button" className={mode === "table" ? "on" : ""} aria-pressed={mode === "table"} onClick={() => setMode("table")}><Icon name="table" size={18} /> 표</button>
            <button type="button" className={mode === "cards" ? "on" : ""} aria-pressed={mode === "cards"} onClick={() => setMode("cards")}><Icon name="grid" size={18} /> 카드</button>
          </div>
        ) : null}
        {wc !== "compact" ? <button type="button" className="btn btn-ghost" onClick={() => window.print()}><Icon name="print" size={18} /> 관리표 출력</button> : null}
      </div>

      <div className={"stats" + (wc !== "compact" ? " stats-5" : "")}>
        <StatTile n={stt.total} label="등원 예정" />
        <StatTile n={stt.done} label="완료" color="var(--color-status-done)" />
        <StatTile n={stt.partial} label="미완료" color="var(--color-status-partial)" />
        <StatTile n={stt.noshow} label="노쇼" color="var(--color-status-noshow)" />
        {wc !== "compact" ? <StatTile n={stt.waiting} label="대기" color="var(--color-status-plan)" /> : null}
      </div>

      <div className="chips filters" role="group" aria-label="상태 거르기">
        {FILTERS.map((f) => (
          <button key={f} type="button" className={"chip" + (filter === f ? " on" : "")} aria-pressed={filter === f} onClick={() => setFilter(f)}>
            {f} <span className="chip-n">{all.filter((s) => match(f, s)).length}</span>
          </button>
        ))}
      </div>

      {list.length === 0 ? (
        <Empty>{all.length === 0 ? "이날 잡힌 개별일정이 없습니다." : "해당하는 학생이 없습니다."}</Empty>
      ) : table ? (
        <SessionTable v={v} list={list} sm={sm} />
      ) : (
        <div className={"card-grid cg-" + wc}>
          {list.map((s) => <SessionCard key={s.id} v={v} s={s} st={sm.get(s.studentId)!} />)}
        </div>
      )}

      <PrintSheet v={v} date={date} list={all} sm={sm} />
    </div>
  );
}

function ItemCheck({ it, s }: { it: Item; s: Session }) {
  return (
    <button type="button" role="checkbox" aria-checked={it.done} className={"check" + (it.done ? " on" : "")} onClick={() => toggleItem(it, s)}>
      <span className="check-box" aria-hidden="true">{it.done ? <Icon name="check" size={18} /> : null}</span>
      <span className="check-week">[{it.week}]</span>
      <span className="check-title">{it.title}</span>
      {it.done ? <span className="check-ok">통과</span> : null}
    </button>
  );
}

function Actions({ v, s, st }: { v: View; s: Session; st: Student }) {
  const items = itemsFor(v, s);
  const closed = s.status !== St.PLAN;
  const anyOpen = items.some((i) => !i.done);
  return (
    <div className="actions">
      <button type="button" className={"tog tog-late" + (s.late ? " on" : "")} aria-pressed={s.late} onClick={() => setLate(s, !s.late)}>
        {s.late ? "✓ 지각" : "지각"}
      </button>
      {!closed ? (
        <>
          <button type="button" className="tog tog-noshow" onClick={() => {
            closeSession(s, St.NOSHOW);
            toast(`${st.name} 노쇼 처리 · 경고카드 1장`, { undo: () => closeSession({ ...s, status: St.NOSHOW }, St.PLAN) });
          }}>노쇼</button>
          <span className="grow" />
          {anyOpen ? (
            <button type="button" className="btn btn-partial" onClick={() => { closeSession(s, St.PARTIAL); go(`register?student=${encodeURIComponent(st.id)}`); }}>미완료 → 다음 일정</button>
          ) : (
            <button type="button" className="btn btn-done" onClick={() => closeSession(s, St.DONE)}>완료</button>
          )}
        </>
      ) : (
        <>
          <span className="grow" />
          {s.status !== St.DONE || anyOpen ? <button type="button" className="btn btn-ghost" onClick={() => closeSession(s, St.PLAN)}>대기로 되돌리기</button> : null}
          {s.status !== St.DONE && upcoming(v, st.id).length === 0 ? (
            <a className="btn btn-ghost" href={`#/register?student=${encodeURIComponent(st.id)}`}>다음 일정 잡기</a>
          ) : null}
        </>
      )}
    </div>
  );
}

function SessionCard({ v, s, st }: { v: View; s: Session; st: Student }) {
  const items = itemsFor(v, s);
  return (
    <article className="scard" style={{ "--accent": statusVar(s.status) } as CSSProperties}>
      <header className="scard-h">
        <span className="time">{s.time}</span>
        <StudentName st={st} />
        <span className="grow" />
        {s.late ? <Pill color="var(--color-late)">지각</Pill> : null}
        <StatusPill status={s.status} />
      </header>
      <p className="muted small meta">등록 {s.createdBy || "-"}{s.closedBy ? ` · 확인 ${s.closedBy}` : ""}{s.note ? ` · ${s.note}` : ""}</p>
      <div className="checks">
        {items.length === 0 ? <p className="muted small">남은 시험지 없음</p> : items.map((it) => <ItemCheck key={it.id} it={it} s={s} />)}
      </div>
      <Actions v={v} s={s} st={st} />
    </article>
  );
}

function SessionTable({ v, list, sm }: { v: View; list: Session[]; sm: Map<string, Student> }) {
  return (
    <div className="table-wrap" data-scroll-x>
      <table className="tbl">
        <thead>
          <tr><th>시간</th><th>학생</th><th>학교</th><th>시험지</th><th>상태</th><th>메모</th><th>처리</th></tr>
        </thead>
        <tbody>
          {list.map((s) => {
            const st = sm.get(s.studentId)!;
            const items = itemsFor(v, s);
            return (
              <tr key={s.id} style={{ "--accent": statusVar(s.status) } as CSSProperties}>
                <td className="time">{s.time}</td>
                <td><StudentName st={st} school={false} /></td>
                <td className="muted nowrap">{st.school}</td>
                <td className="cell-checks">{items.length === 0 ? <span className="muted small">없음</span> : items.map((it) => <ItemCheck key={it.id} it={it} s={s} />)}</td>
                <td><div className="cell-status"><StatusPill status={s.status} />{s.late ? <Pill color="var(--color-late)">지각</Pill> : null}</div></td>
                <td className="small">{s.note || <span className="muted">-</span>}{s.closedBy ? <div className="muted">확인 {s.closedBy}</div> : null}</td>
                <td className="cell-actions"><Actions v={v} s={s} st={st} /></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** 인쇄 전용 당일 관리표(v0.3 Export.dailyHtml 과 같은 구성) — 화면에서는 숨김. 흑백·A4 세로·노쇼 제외·확인 칸은 빈칸 */
function PrintSheet({ v, date, list, sm }: { v: View; date: string; list: Session[]; sm: Map<string, Student> }) {
  const rows = list.filter((s) => s.status !== St.NOSHOW);
  return (
    <section className="print-only" aria-hidden="true">
      <div className="p-logo"><img src="brand/logo_full.png" alt="" /></div>
      <h1>{fmtDate(date)} 클리닉</h1>
      <p className="p-sub">통과해야 하는 시험지 목록 · 총 {rows.length}명 · 출력 {nowHM()}</p>
      <table className="ptbl">
        <thead><tr><th className="p-n">이름</th><th className="p-t">시간</th><th>통과해야 하는 시험지</th><th className="p-c">확인</th></tr></thead>
        <tbody>
          {rows.map((s) => {
            const st = sm.get(s.studentId)!;
            const items = itemsFor(v, s);
            return (
              <tr key={s.id}>
                <td><b>{st.name}</b><br /><span className="p-muted">{st.school}</span></td>
                <td className="p-t">{s.time}</td>
                <td>{items.length ? items.map((it) => <div key={it.id}>☐ [{it.week}] {it.title}{it.done ? <b> 통과</b> : null}</div>) : <span className="p-muted">남은 항목 없음</span>}</td>
                <td />
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}
